import type { TaskBodyProps } from './index';
import { DesignPlanHistory } from './DesignPlanHistory';
import { DesignReviewFooter } from './DesignReviewFooter';
import { TaskSidebarPanel } from './TaskSidebarPanel';
import { RefinementDesignSidebar } from './RefinementDesignSidebar';
import { buildSidebarTabs, DESIGN_TABS } from './taskSidebarTabs';

type DesignReviewVariant = 'refinement' | 'postApproval';

function resolveVariant(taskId: string | undefined): DesignReviewVariant {
    return taskId === 'review-refinement-design' ? 'refinement' : 'postApproval';
}

interface VariantConfig {
    historyTitle?: string;
    historyEmptyText?: string;
    historySubmitType?: string | string[];
    historyPreApprovedType?: string;
    historySince?: string | null;
    renderSidebar: (patientId: string) => React.ReactNode;
}

function buildVariantConfig(
    variant: DesignReviewVariant,
    phaseEnteredAt: string | null
): VariantConfig {
    if (variant === 'refinement') {
        return {
            historyTitle: 'Submitted refinement instructions',
            historyEmptyText: 'No refinement design instructions submitted yet.',
            historySubmitType: 'submit-refinement-for-design',
            historyPreApprovedType: 'refinement-design-pre-approved',
            historySince: phaseEnteredAt,
            renderSidebar: (id) => <RefinementDesignSidebar patientId={id} since={phaseEnteredAt} />
        };
    }

    return {
        historySubmitType: [
            'submit-design-plan',
            'request-post-approval-redesign',
            'reject-post-approval-redesign'
        ],
        renderSidebar: (id) => <TaskSidebarPanel tabs={buildSidebarTabs(id, DESIGN_TABS)} />
    };
}

export function DesignReviewTask({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete
}: TaskBodyProps) {
    const variant = resolveVariant(statusMeta.task?.id);
    const config = buildVariantConfig(variant, c.phaseEnteredAt);

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            {/* Two-column content: submitted Tx instructions (left) + sidebar (right) */}
            <div className="flex-1 min-h-0 flex gap-3 px-4 pt-3 pb-3">
                {/* LEFT - submitted Tx instructions history */}
                <div className="flex-[6] min-w-0 flex flex-col">
                    <DesignPlanHistory
                        patientId={c.id}
                        title={config.historyTitle}
                        emptyText={config.historyEmptyText}
                        submitType={config.historySubmitType}
                        preApprovedType={config.historyPreApprovedType}
                        since={config.historySince}
                    />
                </div>

                {/* RIGHT - variant-specific sidebar */}
                <div className="flex-[4] min-w-0 min-h-0">{config.renderSidebar(c.id)}</div>
            </div>

            <DesignReviewFooter
                caseId={c.id}
                statusMeta={statusMeta}
                onPayloadChange={onPayloadChange}
                onTaskComplete={onTaskComplete}
            />
        </div>
    );
}
